/**
 * 台北日期小工具(2026-10-06)
 *
 * 後台的「今天」(出貨日預設值、匯出檔名)一律是台北日曆日,跟裝置時區無關。
 * 台灣 1980 年後沒有日光節約時間 → 固定 UTC+8,跟後端 `order_service._today_tpe()`
 * (`timezone(timedelta(hours=8))`)同一個定義:同一瞬間,前後端算出同一天。
 *
 * 不要再加 `getTimezoneOffset()`:舊寫法 `now + (getTimezoneOffset() + 480) 分` 在台北時區的裝置上
 * 位移抵消成 0,`toISOString()` 拿到的是 UTC 日期 → 台北 00:00–07:59 變成前一天。
 */

const TPE_OFFSET_MS = 8 * 60 * 60_000;

/** 台北今天的 YYYY-MM-DD(出貨日 input 預設值) */
export function todayTpe(now: Date = new Date()): string {
  // 絕對時間往後推 8 小時,再讀 UTC 的日曆日 = 台北的日曆日;全程不經過裝置時區
  return new Date(now.getTime() + TPE_OFFSET_MS).toISOString().slice(0, 10);
}

/** 台北今天的 YYYYMMDD(匯出檔名用) */
export function todayTpeCompact(now: Date = new Date()): string {
  return todayTpe(now).replace(/-/g, "");
}

// ── 會籍期間預覽(2026-10-06)──────────────────────
// 後台「改等級」選付費等級時,先顯示「將設定為 2026-11-01 ~ 2027-10-31」。
// 規則跟後端 tier_service.compute_membership_period 一樣(以後端實際寫入為準,這裡只是預覽):
//   next_month = 次月 1 日起算;today = 台北今天起算。結束日 = 起算日 + months 個月 − 1 日,月底溢位取該月最後一天。

/** 日曆日加 N 個月(純 y/m/d 運算,不經過裝置時區)。月底溢位取該月最後一天。 */
function addMonths(y: number, m: number, d: number, months: number): { y: number; m: number; d: number } {
  const total = y * 12 + (m - 1) + months;
  const ny = Math.floor(total / 12);
  const nm = (total % 12) + 1;
  const lastDay = new Date(Date.UTC(ny, nm, 0)).getUTCDate(); // 下個月第 0 天 = 本月最後一天
  return { y: ny, m: nm, d: Math.min(d, lastDay) };
}

function ymd(y: number, m: number, d: number): string {
  return `${String(y).padStart(4, "0")}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
}

/** 付費會籍期間預覽。回 {start, end}(YYYY-MM-DD,台北日期)。 */
export function previewMembershipPeriod(
  startMode: "next_month" | "today" = "next_month",
  months = 12,
  now: Date = new Date(),
): { start: string; end: string } {
  const [ty, tm, td] = todayTpe(now).split("-").map(Number);
  const start = startMode === "today" ? { y: ty, m: tm, d: td } : addMonths(ty, tm, 1, 1);
  const afterEnd = addMonths(start.y, start.m, start.d, months);
  // 結束日 = 起算日加 N 個月的前一天
  const end = new Date(Date.UTC(afterEnd.y, afterEnd.m - 1, afterEnd.d - 1));
  return {
    start: ymd(start.y, start.m, start.d),
    end: ymd(end.getUTCFullYear(), end.getUTCMonth() + 1, end.getUTCDate()),
  };
}
